import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useParams, Outlet, useSearchParams } from 'react-router-dom';
import { TourBagMstrPayload } from '@/api/models';
import { useToast } from '@/context/ToastContext';
import { MyPlayerService, TourBagService } from '@/service';
import { ActivePlayerDB, MyPlayerDB, PlayerDB, TourBagDB } from '@/db/repositories';
import { IActivePlayer } from '@/db/repositories/ActivePlayerRepo';
import { getUser } from '@/library/UserSessionHelper';
import { modules, ModuleName, Action } from '@/constants';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import UserSessionModel from '@/db/models/UserSessionModel';
import { UserSettingsHelper } from '@/library';

export type UserPermissions = Pick<
  UserSessionModel,
  'menuSettings' | 'tourReportSettings' | 'tourPageSettings' | 'moduleSettings' | 'contractSettings' | 'tourPlayerSettings' | 'tourBagSettings' | 'ordersReportSettings'
>;

export type PlayerLoadError = {
  message: string;
  type: 'sync' | 'generic';
} | null;

/* eslint-disable no-unused-vars */
interface PlayerContextValue {
  player: IActivePlayer | null;
  setPlayer: (player: IActivePlayer) => Promise<void>;
  clearPlayer: () => Promise<void>;
  activeModule: string;
  setModule: (module: ModuleName) => void;
  isMyPlayer: boolean;
  handleMyPlayer: (add: boolean) => Promise<void>;
  tourBag: TourBagMstrPayload[] | undefined;
  updateTourBag: (tourBag: TourBagMstrPayload, action: Action, clubId?: number) => Promise<TourBagMstrPayload | undefined>;
  updateLocalTourBag: (bags: TourBagMstrPayload[]) => Promise<void>;
  permissions: UserPermissions | undefined;
  moduleOrder: ModuleName[];
  setModuleOrder: (order: ModuleName[]) => void;
  isLoading: boolean;
  error: PlayerLoadError;
}
/* eslint-enable no-unused-vars */

const isModuleName = (value: string): value is ModuleName => modules.includes(value as ModuleName);

const ActivePlayerContext = createContext<PlayerContextValue | undefined>(undefined);

export const useActivePlayer = () => {
  const context = useContext(ActivePlayerContext);
  if (!context) throw new Error('useActivePlayer must be used within PlayerProvider');
  return context;
};

interface ActivePlayerProviderProps {
  children: ReactNode;
}

export const ActivePlayerProvider = ({ children }: ActivePlayerProviderProps) => {
  const { showToast } = useToast();
  const { playerId } = useParams<{ playerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [playerState, setPlayerState] = useState<IActivePlayer | null>(null);
  const [activeModule, setActiveModule] = useState<ModuleName>(modules[0]);
  const [isMyPlayer, setIsMyPlayer] = useState(false);
  const [tourBag, setTourBag] = useState<TourBagMstrPayload[]>();
  const [permissions, setPermissions] = useState<UserPermissions>();
  const [moduleOrder, setModuleOrder] = useState<ModuleName[]>([...modules]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<PlayerLoadError>(null);
  const isOnline = useOnlineStatus();
  const { syncStatus } = useBackgroundSync();

  // load active player from IndexedDB once on mount
  useEffect(() => {
    const loadPlayer = async () => {
      setError(null);
      setIsLoading(true);

      try {
        let currentPlayer = await ActivePlayerDB.get();
        // if no playerId return
        if (!playerId) return null;
        // if no active player but playerId exists, fetch player from DB for direct URL access/ bookmark
        if (!currentPlayer) {
          const dbPlayer = await PlayerDB.get(Number(playerId)); // Dexie table index is numeric so we cast
          if (!dbPlayer) {
            throw new Error('Player not found in local database.');
          }
          await ActivePlayerDB.set(dbPlayer);
          currentPlayer = dbPlayer;
        }
        if (!currentPlayer) throw new Error('Cannot set active player: no player data');

        // set is my player
        const myPlayers = await MyPlayerDB.list();
        const isMyPlayer = myPlayers.some((myPlayer) => myPlayer.playerId === currentPlayer.playerId);

        // fetch tourbag
        const tourBag = await TourBagService.getTourBagData(currentPlayer.playerId);
        if (!tourBag) {
          throw new Error('Tour bag data missing.');
        }

        // get module settings
        const user = await getUser();
        if (!user) {
          throw new Error('User data missing.');
        }
        setPermissions({
          menuSettings: user.menuSettings,
          tourReportSettings: user.tourReportSettings,
          tourPageSettings: user.tourPageSettings,
          moduleSettings: user.moduleSettings,
          contractSettings: user.contractSettings,
          tourPlayerSettings: user.tourPlayerSettings,
          tourBagSettings: user.tourBagSettings,
          ordersReportSettings: user.ordersReportSettings,
        });

        // Initialize subnav order
        const userSubnavOrder = UserSettingsHelper.getParsedSubnavOrder(user);
        setModuleOrder(userSubnavOrder);

        // initialize states
        setIsMyPlayer(isMyPlayer);
        setPlayerState(currentPlayer);

        // handle initial module
        let initialModule: ModuleName = currentPlayer.activeModule ?? userSubnavOrder[0];
        const moduleParam = searchParams.get('module');
        if (moduleParam && isModuleName(moduleParam)) {
          initialModule = moduleParam;
          setSearchParams({}, { replace: true }); // remove query param after usage
        }

        const initialAvailable = UserSettingsHelper.getAvailableModules(userSubnavOrder, isOnline, user.tourPageSettings);
        if (!initialAvailable.includes(initialModule)) {
          initialModule = initialAvailable[0] ?? userSubnavOrder[0];
        }
        setActiveModule(initialModule);
        setTourBag(tourBag);
      } catch (err) {
        console.error('Failed to load active player context:', err);

        if (syncStatus === 'error') {
          setError({
            message: 'Background sync failed.',
            type: 'sync',
          });
        } else {
          setError({
            message: 'There was an error retrieving player data.',
            type: 'generic',
          });
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadPlayer();

    return () => {
      // reset tour bag on component unmount
      setIsLoading(true);
      setTourBag([]);
      ActivePlayerDB.clear();
    };
  }, [playerId, showToast]);

  const setModule = async (module: ModuleName) => {
    if (!playerState) throw new Error('Cannot set module: no active player.');
    // update state immediately so the UI responds instantly, even when IndexedDB is under heavy load from background sync.
    setActiveModule(module);
    const activePlayer: IActivePlayer = {
      ...playerState,
      activeModule: module,
    };
    await ActivePlayerDB.set(activePlayer);
  };

  const setPlayer = async (newPlayer: IActivePlayer) => {
    const activeModule: ModuleName = newPlayer.activeModule ?? 'Driver';
    const activePlayer: IActivePlayer = {
      ...newPlayer,
      activeModule,
    };
    await ActivePlayerDB.set(activePlayer);
    setPlayerState(activePlayer);
    setActiveModule(activeModule);
  };

  const clearPlayer = async () => {
    await ActivePlayerDB.clear();
    setPlayerState(null);
  };

  const handleMyPlayer = async (checked: boolean) => {
    if (!playerState) throw new Error('Cannot set module: no active player.');
    try {
      if (checked) {
        await MyPlayerService.addMyPlayer(playerState);
      } else {
        await MyPlayerService.removeMyPlayer(playerState);
      }
      // Update local state
      setIsMyPlayer(checked);
    } catch (error) {
      console.error('Failed to update MyPlayer state:', error);
      const action = checked ? 'add' : 'delete';
      showToast({ message: `Failed to ${action} player ${playerState.fullName} to MyPlayers` });
      throw error;
    }
  };

  const updateLocalTourBag = async (bags: TourBagMstrPayload[]) => {
    if (!playerState) throw new Error('Cannot update tour bag: no active player.');
    await TourBagDB.update({ playerId: playerState.playerId, bags });
    setTourBag(bags);
  };

  const updateTourBag = async (updatedBag: TourBagMstrPayload, action: Action, clubId?: number) => {
    if (!playerState) throw new Error('Cannot set module: no active player.');
    try {
      const result = await TourBagService.saveTourBagData(playerState.playerId, updatedBag, action, clubId);
      // Update local state by merging the singular result back into the array
      if (result) {
        setTourBag((prev) => {
          if (!prev) return [result];
          const exists = prev.some((bag) => bag.headTypeDescription === result.headTypeDescription);
          if (exists) {
            return prev.map((bag) => (bag.headTypeDescription === result.headTypeDescription ? result : bag));
          }
          return [...prev, result];
        });
      }
      return result;
    } catch (error) {
      console.error('Failed to update TourBag:', error);
      showToast({ message: `Failed to update player ${playerState.fullName}'s TourBag` });
      // TODO: Save error to ErrorLogRepo
      throw error;
    }
  };

  return (
    <ActivePlayerContext.Provider
      value={{
        player: playerState,
        setPlayer,
        clearPlayer,
        activeModule,
        setModule,
        isMyPlayer,
        handleMyPlayer,
        tourBag,
        updateTourBag,
        updateLocalTourBag,
        permissions,
        moduleOrder,
        setModuleOrder,
        isLoading,
        error,
      }}
    >
      {children}
    </ActivePlayerContext.Provider>
  );
};

export default function ActivePlayerProviderWrapper() {
  return (
    <ActivePlayerProvider>
      <Outlet />
    </ActivePlayerProvider>
  );
}
